import React, { useRef, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes } from "react-icons/fa";
import SimpleAssistantPage from "../pages/SimpleAssistant";

export default function VoiceAssistantOverlay({
  isOpen,
  onClose,
  autoStart = false,
}) {
  const assistantRef = useRef(null);
  const modalRef = useRef(null);

  // Default scaling is 1 (no scale). We shrink the content if the container is too small.
  const [scale, setScale] = useState(1);

  // The space (in px) to reserve so the close button doesn’t overlap the content.
  const CLOSE_BUTTON_BUFFER = 56; // Adjust as needed

  const handleClose = () => {
    if (assistantRef.current) {
      assistantRef.current.disconnect();
    }
    onClose();
  };

  useEffect(() => {
    function updateScale() {
      if (!modalRef.current) return;

      const containerHeight = modalRef.current.offsetHeight;
      // Scale so that content height <= (containerHeight - CLOSE_BUTTON_BUFFER).
      const newScale =
        (containerHeight - CLOSE_BUTTON_BUFFER) / containerHeight;

      // Only scale down, never blow up bigger than 1:1.
      setScale(newScale < 1 ? newScale : 1);
    }

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-gray-100 bg-opacity-90 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            ref={modalRef}
            className="bg-white rounded-xl shadow-xl w-full h-full
                       md:w-3/4 md:h-3/4 relative overflow-hidden flex flex-col"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <button
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-4 right-4 bg-gray-300 hover:bg-gray-400 
                         text-white rounded-full w-10 h-10 flex items-center
                         justify-center focus:outline-none transition-colors z-10"
            >
              <FaTimes className="w-4 h-4" />
            </button>

            {/* 
              FLEX CONTAINER to center the scaled content both vertically and horizontally.
              .flex-1 ensures it occupies all available space; items-center + justify-center
              put the child <div> in the middle.
            */}
            <div className="flex-1 flex items-center justify-center">
              <div
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: "center center",
                }}
              >
                <SimpleAssistantPage ref={assistantRef} autoStart={autoStart} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
